<template>
    <b-modal
            :visible="show"
            @hidden="hideEvents"
            title="Approve Actions"
            :id="modalId"
            scrollable
    >
        <vue-element-loading :active="loading" :is-full-screen="false"/>
        <b-alert
                :show="invalidNodes.length > 0"
                variant="warning"
        >
            Please note that the following actions were unselected
            because their status is
            <span v-if="invalidStatus === statuses.pending" >not</span>
            '{{ invalidStatus | uppercaseFirst }}':
            <div class="font-weight-bold">
                #{{ invalidNodeIds.join(', #') }}
            </div>
        </b-alert>
        <b-form>
            <b-form-textarea
                    class="mb-3"
                    placeholder="Write a note... (not required)"
                    rows="6"
                    v-model="form.note"
            >
            </b-form-textarea>

            <vue-dropzone
                    id="fileDrop"
                    ref="dropzone"
                    :options="dropOptions"
                    @vdropzone-sending="attachParams"
                    @vdropzone-success="success"
                    @vdropzone-error="error"
                    @vdropzone-mounted="dropzoneInitialized = true"
                    @vdropzone-files-added="onFilesAdd"
                    @vdropzone-removed-file="onFilesRemoved"
                    @vdropzone-file-added="checkForDuplicates"
            >
            </vue-dropzone>
        </b-form>

        <confirmation-warning
                v-if="showUnlockButton"
                :numberOfItems="this.nodeIds.length"
                @unlocked="unlocked = true">
        </confirmation-warning>

        <template slot="modal-footer">
            <b-button
                    variant="outline-secondary"
                    @click="hideModal">
                Cancel
                <font-awesome-icon
                        :icon="['far', 'times']">
                </font-awesome-icon>
            </b-button>
            <b-button
                    class="btn-arrow"
                    variant="yellow"
                    @click="approveActions"
                    :disabled="approveDisabled"
            >
                Approve
                <font-awesome-icon
                        :icon="['far', 'arrow-right']">
                </font-awesome-icon>
            </b-button>
        </template>
    </b-modal>
</template>

<script>
import CaretIcon from '@/components/Form/CaretIcon';
import ModalMixin from '@/components/Modal/ModalMixin';
import vueDropzone from 'vue2-dropzone';
import ActionStatus from '@/services/Action/ActionStatus';
import Action from '@/services/Action/Action';
import ConfirmationWarning from '@/components/Notifications/ConfirmationWarning';

export default {
  name: 'Approve-Modal',
  components: {
    CaretIcon,
    vueDropzone,
    ConfirmationWarning,
  },
  props: {
    invalidNodes: {
      default: [],
    },
    invalidStatus: {
      type: String,
      default: () => ActionStatus.PENDING_APPROVAL(),
    },
    type: {
      required: true,
    },
    model: {
      required: false,
    },
  },
  mixins: [ModalMixin],
  data() {
    return {
      modalId: 'approveModal',
      loading: false,
      displayedNotification: false,
      unlocked: false,
      maxNumberOfSelectedActions: 300,
      statuses: {
        pending: ActionStatus.PENDING_APPROVAL(),
      },
      form: {
        note: '',
      },
      filesAdded: 0,
      dropzoneInitialized: false,
      dropOptions: {
        url: () => this.approveUrl,
        dictRemoveFile: '❌',
        dictDefaultMessage: 'Drop files here to upload, or click to <span class="underline">select</span> a file to upload.',
        addRemoveLinks: true,
        autoProcessQueue: false,
        uploadMultiple: true,
        parallelUploads: 10,
        thumbnailWidth: 125,
        thumbnailHeight: 125,
        acceptedFiles: '.csv,.xls,.xlsx,.doc,.docx,.pdf,.jpg,.jpeg,.png',
        headers: {
          Authorization: `Bearer ${this.$store.getters.authToken}`,
        },
        renameFile(file) {
          const fileParts = file.name.split('.');
          const extension = fileParts[fileParts.length -1].toLowerCase();
          fileParts[fileParts.length -1] = extension;
          return fileParts.join('.');
        },
      },
    };
  },
  computed: {
    allPending() {
      return this.nodes
        .filter(node => node.data.status !== ActionStatus.PENDING_APPROVAL())
        .length === 0;
    },
    approveDisabled() {
      if (this.loading
          || (this.requiresApproval && !this.unlocked)
      ) {
        return true;
      }

      if (this.allPending) {
        return false;
      }

      if (!this.adminBulk) {
        return false;
      }

      return !this.hasFiles();
    },
    clientActionId() {
      return this.nodeIds[0];
    },
    invalidNodeIds() {
      return this.invalidNodes.map(node => node.data.clientActionId);
    },
    isBulk() {
      return this.nodeIds.length > 1;
    },
    approveUrl() {
      return `${api.defaults.baseURL}/actions/${this.type}/approve/bulk`;
    },
    adminBulk() {
      return this.$can(
        this.$perm.ACTIONS_BULK_APPROVE_ADMIN,
        this.$perm.PERMISSION_TYPE_ACTION,
      );
    },
    requiresApproval() {
      return this.nodeIds.length > this.maxNumberOfSelectedActions;
    },
    showUnlockButton() {
      return this.requiresApproval;
    },
  },
  created() {
    // Change dropzone placeholder message
    if (!this.adminBulk || this.allPending) {
      this.dropOptions.dictDefaultMessage = `${this.dropOptions.dictDefaultMessage} <br/>This is not required.`;
    }
  },
  filters: {
    uppercaseFirst(string) {
      if (!string) {
        return '';
      }

      return string.charAt(0)
        .toUpperCase() + string.slice(1);
    },
  },
  methods: {
    approveActions() {
      if (!this.validate()) {
        return;
      }

      if (!this.hasFiles()) {
        this.approveWithNoFiles();
      }

      this.loading = true;
      this.$refs.dropzone.processQueue();
    },

    getModel() {
      if (this.model) {
        return this.model;
      }

      return this.$route.meta.model;
    },

    async approveWithNoFiles() {
      this.loading = true;
      const clientActionIds = this.nodeIds;
      const { note } = this.form;
      const model = this.getModel();

      try {
        const { data } = await model.bulkApprove({ clientActionIds, note });

        this.success(null, data);
      } catch (e) {

      } finally {
        this.loading = false;
      }
    },

    success(file, response) {
      if (this.displayedNotification) {
        return;
      }

      // Bulk
      this.approveBulkSuccess(response);
    },

    error() {
      this.$notify({
        title: 'Error',
        text: 'Something went wrong. Please try again later.',
        type: 'error',
      });
      this.complete();
    },

    complete() {
      this.hideModal();
      this.loading = false;
    },

    approveBulkSuccess(response) {
      const { total, valid, invalid } = response.counts;
      const hasInvalid = parseInt(invalid, 2) > 0;

      // This is the default behaviour
      let title = 'Actions processed';
      let text = `${valid} Actions have been successfully approved.`;
      let type = 'success';

      if (hasInvalid) {
        type = 'warn';
        text += ` ${invalid} Actions failed to be approved based on their status.`;
      }

      // If this is the admin bulk, we dispatch it as a job
      // So we have to notify them to check their emails

      const successText = `<p>${valid} Actions have been queued to be approved.</p>
            <p>The time to complete will depend on the number of Actions selected.</p>
            <p>Please contact Riskhub Support if you do not receive an email within 30 minutes</p>
        `;
      if (this.adminBulk) {
        title = 'Actions processing';
        text = successText;
        type = 'success';

        if (hasInvalid) {
          type = 'warn';
          text = `<p>${invalid} Actions have failed to be approved based on their status.</p>`;

          if (valid > 0) {
            text += ` ${successText}`;
          }
        }
      }

      this.$notify({
        duration: 10000,
        title,
        text,
        type,
      });

      // Refresh
      if (!this.adminBulk) {
        setTimeout(() => {
          this.$store.dispatch('refreshActions');
        }, 1500);
      }
      this.complete();
    },

    validate() {
      if (this.requiresApproval && !this.unlocked) {
        return false;
      }

      if (!this.adminBulk || this.allPending) {
        return true;
      }

      if (!this.hasFiles()) {
        this.$notify({
          title: 'Error',
          text: 'Please upload a file as an approval document.',
          type: 'error',
        });
        return false;
      }

      return true;
    },

    attachParams(file, xhr, formData) {
      this.nodeIds.forEach((id) => {
        formData.append('clientActionIds[]', id);
      });

      formData.append('note', this.form.note);
    },

    getFiles() {
      return this.$refs.dropzone.getQueuedFiles();
    },

    hasFiles() {
      if (!this.dropzoneInitialized) {
        return false;
      }

      return this.filesAdded > 0;
    },

    onFilesAdd() {
      this.refreshFilesAdded();
    },

    onFilesRemoved() {
      this.refreshFilesAdded();
    },

    refreshFilesAdded() {
      this.filesAdded = this.getFiles().length;
    },

    hideModalEvents() {
      this.form.note = '';
      this.filesAdded = 0;
    },

    checkForDuplicates(file) {
      const existingFiles = this.$refs.dropzone.getAcceptedFiles();
      const duplicate = existingFiles.find(f =>
        f !== file &&
          f.name === file.name &&
          f.size === file.size &&
          f.type === file.type);

      if (duplicate) {
        this.$refs.dropzone.removeFile(file);
        this.$notify({
          title: 'Duplicate file',
          text: `${file.name} has already been added.`,
          type: 'warn',
        });
      }
    },
  },
};
</script>

<style lang="scss">
    .dz-progress {
        display: none;
    }

    .vue-dropzone .dz-preview {
        .dz-remove {
            top: 0;
            right: 0;
            opacity: 1;
            background: rgba(78, 115, 138, 0.95);
            bottom: initial;
            padding: 5px 7.5px;
            border: 0;
        }
    }
</style>
